import React from "react";
import Topbar from "../../topbar/Topbar";
import styled from "styled-components";
import {
  TextVariant,
  TypographyNew,
} from "../../../product/global/typography/typography";
import SliderControlled from "../../../product/global/formFields/SliderControlled";
import { useForm } from "react-hook-form";
import SelectNewController from "../../../product/global/formFields/dynamic/SelectNew";
import CheckboxControlled from "../../../product/global/formFields/CheckboxContorolled";
import { useBreakpoint, useQueryParams } from "../../../product/utils/hooks";
import { getPerMonth, getPerYear, modules, modulesMap } from "./modules";
import CurrencyInput from "../../../product/global/formFields/CurrencyInput";
import Footer from "../../footer/Footer";
import { useTranslation } from "react-i18next";
import PageReworking from "../../PageReworking";

const Container = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.colors.misc3};
`;

const Body = styled.div`
  width: 100%;
  margin: 0 auto;
  padding-top: 65px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 100px;

  @media (min-width: 1400px) {
    width: 1400px;
  }
`;

const PricesTable = styled.ul`
  list-style-type: none;
  padding: 0;
  border-radius: 10px;
  box-shadow: 0 3px 6px #00000029;
  margin: 85px 0 100px;
  background-color: ${(props) => props.theme.colors.white};
  width: calc(100vw - 30px);
  overflow: hidden;

  @media (min-width: 1280px) {
    width: auto;
  }
`;

const formatPrice = (price: number): string => {
  return price.toFixed(2).replace(".", ",").concat(" €");
};

const PricesTableItem = styled.li<{ active: boolean }>`
  display: grid;
  grid-template-columns: 1fr 50px 1fr 50px 40px;
  padding: 22px 15px;
  border-bottom: 1px solid #d4d6da;
  align-items: center;
  background-color: ${({ active }) => (active ? "#f2f5f7" : "none")};

  @media (min-width: 600px) {
    padding: 35px 30px;
    grid-template-columns: 25px 200px 1fr 1fr 1fr 40px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: 25px 185px 400px 100px 150px 150px 100px;
    padding: 22px 60px;

    :hover {
      background-color: #f2f5f7;
      cursor: pointer;
    }
  }
`;

const PricesTableHead = styled.li`
  display: grid;
  grid-template-columns: 1fr 50px 1fr 50px 40px;
  padding: 35px 15px;
  border-bottom: 1px solid #d4d6da;

  @media (min-width: 600px) {
    padding: 35px 30px;
    grid-template-columns: 200px 1fr 1fr 1fr 40px;
    grid-gap: 30px;
  }

  @media (min-width: 1280px) {
    padding: 35px 60px;
    grid-template-columns: 205px 400px 100px 150px 150px 100px;
    grid-gap: 0;
  }
`;

const PricesTableFooter = styled.li`
  display: flex;
  justify-content: flex-end;
  padding: 35px 65px;
`;

const StyledHeader = styled(TypographyNew)`
  font-weight: normal;
  margin: 85px 20px;

  @media (min-width: 600px) {
    margin: 85px 65px;
  }

  @media (min-width: 1280px) {
    margin: 85px 0;
  }
`;

const SeatsField = styled(CurrencyInput)`
  width: 75px;

  @media (min-width: 1280px) {
    width: 55px;
  }
`;

const IntervalField = styled(SelectNewController)`
  width: 80px;
  height: 40px;

  > div > div {
    width: 100%;
  }

  @media (min-width: 1280px) {
    width: 135px;
  }
`;

const StyledSlider = styled(SliderControlled)`
  width: calc(100vw - 60px);

  @media (min-width: 600px) {
    width: 50vw;
  }

  @media (min-width: 1280px) {
    width: 570px;
  }

  > div {
    width: 100%;
  }

  input[type="range"] {
    width: calc(100% - 50px);
  }
`;

const Prefix = styled.span`
  font-weight: normal;
`;

type FormState = {
  employees: string;
  modules: Record<
    string,
    { interval: string; seats: string; selected: boolean }
  >;
};

const defaultState: FormState = {
  employees: "80",
  modules: modules.reduce((acc, module) => {
    acc[module.id] = {
      seats: "80",
      interval: "year",
      selected: module.id === "dpf",
    };
    return acc;
  }, {} as FormState["modules"]),
};

const calculateModulePrice = (
  interval: "year" | "month",
  modulePrice: {
    upperLimit: number;
    lowerLimit: number;
    speedOfDecline?: number;
  },
  count: string
): { total: number; perMonth: number } => {
  const priceFn = interval === "year" ? getPerYear : getPerMonth;
  const price = priceFn(
    modulePrice?.upperLimit,
    modulePrice?.lowerLimit,
    count,
    modulePrice.speedOfDecline
  );
  const moduleSeats = parseInt(count);
  const seats = Number.isNaN(moduleSeats) ? 0 : moduleSeats;
  const perMonth = interval === "year" ? price / 12 : price;
  const perYear = interval === "year" ? 12 : 1;

  return { total: seats * Number(perMonth.toFixed(2)) * perYear, perMonth };
};

const calculateSum =
  (modulesState: FormState["modules"]) =>
  (interval: "year" | "month", employees: string) => {
    return Object.keys(modulesState).reduce((acc, item) => {
      if (
        modulesState[item].interval === interval &&
        modulesState[item].selected
      ) {
        const modulePrice = modulesMap[item]?.price;
        const result = calculateModulePrice(interval, modulePrice, employees);

        return acc + result.total;
      }

      return acc;
    }, 0);
  };

const Prices = (): JSX.Element => {
  const params = useQueryParams();
  const { t } = useTranslation("homepage");
  const { control, watch, setValue } = useForm<FormState>({
    defaultValues: defaultState,
  });
  const { isDesktop, isMobile } = useBreakpoint();
  const modulesState = watch("modules");
  const employees = watch("employees");
  const getSum = calculateSum(modulesState);
  const monthSum = getSum("month", employees);
  const yearSum = getSum("year", employees);
  const monthStr =
    monthSum > 0 ? `${formatPrice(monthSum)} ${t("prices.perMonth")}` : "";
  const yearStr =
    yearSum > 0 ? `${formatPrice(yearSum)} ${t("prices.perYear")}` : "";
  const total =
    monthSum > 0 && yearSum > 0
      ? `${monthStr} + ${yearStr}`
      : `${monthStr} ${yearStr}`;

  if (!params.has("enabled")) {
    return <PageReworking />;
  }

  return (
    <div>
      <Topbar />
      <Container>
        <Body>
          <StyledHeader color="#141414" align="center" variant={TextVariant.h2}>
            {t("prices.title")}
          </StyledHeader>
          <StyledSlider
            control={control}
            defaultValue={1}
            fieldName="employees"
            label={t("prices.subtitle")}
            legend={""}
            max={3000}
            min={10}
            stepSize={1}
            labelComponent={
              <SeatsField
                control={control}
                defaultValue={"1"}
                fieldName={"employees"}
              />
            }
          />
          <PricesTable>
            <PricesTableHead>
              <TypographyNew variant={TextVariant.body1}>
                {t("prices.module")}
              </TypographyNew>
              {isDesktop && (
                <TypographyNew variant={TextVariant.body1}>
                  {t("prices.description")}
                </TypographyNew>
              )}
              <TypographyNew variant={TextVariant.body1}>
                {t("prices.seats")}
              </TypographyNew>
              <TypographyNew variant={TextVariant.body1}>
                {t("prices.interval")}
              </TypographyNew>
              <TypographyNew variant={TextVariant.body1}>
                {t("prices.price")}
              </TypographyNew>
              <TypographyNew variant={TextVariant.body1}>
                {t("prices.sum")}
              </TypographyNew>
            </PricesTableHead>
            {modules.map((item) => {
              const moduleItem = modulesState[
                item.id
              ] as FormState["modules"][0];
              const interval = moduleItem.interval as "year" | "month";
              const prefix =
                interval !== "year"
                  ? t("prices.perMonth")
                  : t("prices.perYear");

              const { total, perMonth } = calculateModulePrice(
                interval,
                item.price,
                employees
              );
              return (
                <PricesTableItem
                  onClick={() => {
                    if (item.id === "dpf") return;
                    setValue(
                      `modules.${item.id}.selected`,
                      !moduleItem.selected
                    );
                  }}
                  active={moduleItem.selected}
                  key={item.id}
                >
                  {!isMobile && (
                    <CheckboxControlled
                      control={control}
                      fieldName={`modules.${item.id}.selected`}
                      disabled={item.id === "dpf"}
                    />
                  )}
                  <TypographyNew marginRight="10px">{item.name}</TypographyNew>
                  {isDesktop && (
                    <TypographyNew
                      marginRight="10px"
                      variant={TextVariant.body2}
                    >
                      {item.description}
                    </TypographyNew>
                  )}
                  <TypographyNew marginLeft="15px" variant={TextVariant.body2}>
                    {employees}
                  </TypographyNew>
                  <IntervalField
                    responsive
                    control={control}
                    fieldName={`modules.${item.id}.interval`}
                    placeholder={""}
                    menuItems={[
                      { label: `${t("prices.yearly")} (-10%)`, value: "year" },
                      { label: t("prices.monthly"), value: "month" },
                    ]}
                  />
                  <TypographyNew variant={TextVariant.body2}>
                    {formatPrice(perMonth)}
                  </TypographyNew>
                  <TypographyNew>
                    {`${formatPrice(total)} `}
                    {isDesktop && <Prefix>{prefix}</Prefix>}
                  </TypographyNew>
                </PricesTableItem>
              );
            })}
            <PricesTableFooter>
              <TypographyNew>
                <Prefix>{`${t("prices.total")}: `}</Prefix>
                {total.trim() || "0.00"}
              </TypographyNew>
            </PricesTableFooter>
          </PricesTable>
          <TypographyNew
            marginLeft="85px"
            marginRight="85px"
            variant={TextVariant.caption}
          >
            {
              "Um ein Modul nutzen zu können, benötigt jeder Mitarbeiter pro Monat und Modul eine Monatslizenz. Wenn die Nutzung während eines Monats beginnt, muss eine vollständige Monatslizenz bezahlt werden. Es gibt keine anteilige Berechnung."
            }
          </TypographyNew>
          <br />
          <TypographyNew
            marginLeft="85px"
            marginRight="85px"
            variant={TextVariant.caption}
          >
            {
              "Wenn Sie sich für die jährliche Zahlung entscheiden, kaufen Sie 12 Monatslizenzen pro Mitarbeiter und Modul im Voraus. Die Lizenzen sind monatsspezifisch und es wird ein Rabatt gewährt. Wenn in einem Monat mehr Lizenzen benötigt werden als im Voraus gekauft, werden die zusätzlichen Lizenzen zum Monatspreis nachberechnet. Wenn nicht alle Lizenzen verwendet werden, verfallen die ungenutzten Lizenzen."
            }
          </TypographyNew>
        </Body>
      </Container>
      <Footer />
    </div>
  );
};

export default Prices;
